import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Modal, InputGroup, ListGroup, Badge } from 'react-bootstrap';
import { FaArrowLeft, FaUser, FaHome, FaFileContract, FaCheckCircle, FaSearch, FaUserPlus, FaTimes } from 'react-icons/fa';
import axiosInstance from '../../helpers/api';
import { useNavigate } from 'react-router-dom';

interface NewTransactionProps {
  onBack?: () => void;
  onTransactionCreated?: (transactionId: string) => void;
}

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  lead_id?: string;
  leadId?: string; // Backend returns camelCase
}

interface Lead {
  id: string;
  lead_first_name: string;
  lead_last_name?: string;
  lead_email: string;
  lead_phone: string;
  lead_source?: string;
  lead_status?: {
    status?: string;
  };
}

const NewTransactionEnhanced: React.FC<NewTransactionProps> = ({ onBack, onTransactionCreated }) => {
  const navigate = useNavigate();
  const clientSearchRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  
  // Client search states
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  
  // Add client modal states
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [leadSearchTerm, setLeadSearchTerm] = useState('');
  const [searchingLeads, setSearchingLeads] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  // Removed brokerageId as it's not used in the current flow
  const [leadType, setLeadType] = useState<number | null>(null);
  const [leadStatus, setLeadStatus] = useState<number | null>(null);
  
  const [formData, setFormData] = useState({
    type: 'residential_sale',
    client_id: '',
    client_type: '',
    property_address: '',
    property_city: '',
    property_state: '',
    property_zip: '',
    price: '',
    notes: ''
  });

  const [newClientForm, setNewClientForm] = useState({
    name: '',
    email: '',
    phone: '',
    relationshipType: 'buyer' as 'buyer' | 'seller' | 'both' | 'tenant' | 'landlord' | 'other',
    transactionType: 'residential_sale' as 'residential_sale' | 'residential_rental' | 'commercial_sale' | 'commercial_rental' | 'referral'
  });

  useEffect(() => {
    // Don't fetch all clients on load - we'll search as user types
    // Removed fetchBrokerageInfo as we don't use brokerageId
  }, []);

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (clientSearchRef.current && !clientSearchRef.current.contains(event.target as Node)) {
        setShowClientDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);



  // Search leads from server when user types (like the modal does)
  const searchMainFormLeads = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setClientSearchResults([]);
      setShowClientDropdown(false);
      return;
    }
    
    setLoadingClients(true);
    try {
      console.log('🔍 Searching leads with term:', searchTerm);
      const searchUrl = `/leads?search=${encodeURIComponent(searchTerm)}&limit=20`;
      console.log('🔍 Search URL:', searchUrl);
      
      const response = await axiosInstance.get('/leads', {
        params: {
          search: searchTerm,
          limit: 20
        }
      });
      
      console.log('🔍 Search response:', response.data);
      
      if (response.data.success) {
        // Match the modal's data extraction logic exactly
        const leadsArray = response.data.data?.leads || response.data.data || response.data.leads || [];
        console.log('🔍 Extracted leads array:', leadsArray);
        
        const transformedResults = leadsArray.map((lead: any) => ({
          id: lead.id,
          lead_id: lead.id,
          name: `${lead.lead_first_name || ''} ${lead.lead_last_name || ''}`.trim() || lead.lead_email || 'Unknown',
          email: lead.lead_email || '',
          phone: lead.lead_phone || ''
        }));
        
        console.log(`🔍 Found ${transformedResults.length} matches`);
        setClientSearchResults(transformedResults);
        setShowClientDropdown(true);
      }
    } catch (error) {
      console.error('Error searching leads:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  // Debounce search - same as modal
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (clientSearchTerm.length > 2) {
        searchMainFormLeads(clientSearchTerm);
      } else if (clientSearchTerm.length === 0) {
        setClientSearchResults([]);
        setShowClientDropdown(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [clientSearchTerm]);

  const selectClient = (client: Client) => {
    setSelectedClient(client);
    setFormData({ ...formData, client_id: client.lead_id || client.id });
    setClientSearchTerm(client.name);
    setShowClientDropdown(false);
  };

  const clearClientSelection = () => {
    setSelectedClient(null);
    setFormData({ ...formData, client_id: '' });
    setClientSearchTerm('');
  };

  // Lead search functionality
  const searchLeads = useCallback(async (searchValue?: string) => {
    const termToSearch = searchValue || leadSearchTerm;
    if (!termToSearch.trim()) {
      setLeads([]);
      return;
    }
    
    setSearchingLeads(true);
    try {
      const response = await axiosInstance.get('/leads', {
        params: {
          search: termToSearch,
          limit: 10
        }
      });
      
      if (response.data.success) {
        const leadsData = response.data.data?.leads || response.data.data || response.data.leads || [];
        setLeads(leadsData);
      }
    } catch (error) {
      console.error('Error searching leads:', error);
    } finally {
      setSearchingLeads(false);
    }
  }, [leadSearchTerm]);

  // Debounce search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (leadSearchTerm.length > 2) {
        searchLeads(leadSearchTerm);
      } else if (leadSearchTerm.length === 0) {
        setLeads([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [leadSearchTerm, searchLeads]);

  const selectLeadForClient = (lead: Lead) => {
    setSelectedLead(lead);
    const fullName = `${lead.lead_first_name} ${lead.lead_last_name || ''}`.trim();
    setNewClientForm({
      ...newClientForm,
      name: fullName,
      email: lead.lead_email || '',
      phone: lead.lead_phone || ''
    });
    
    setLeadSearchTerm('');
    setLeads([]);
  };

  const resetAddClientForm = () => {
    setNewClientForm({
      name: '',
      email: '',
      phone: '',
      relationshipType: 'buyer',
      transactionType: 'residential_sale'
    });
    setSelectedLead(null);
    setLeads([]);
    setLeadSearchTerm('');
  };

  const addNewClient = async () => {
    try {
      // Get lead types if not already loaded
      if (!leadType) {
        try {
          const typesResponse = await axiosInstance.get('/leads/types');
          const types = typesResponse.data.data.leadTypes || [];
          if (types.length > 0) {
            // Use the first lead type as default
            setLeadType(types[0].id);
            // Get statuses for this type
            const statusResponse = await axiosInstance.get(`/leads/status/${types[0].id}`);
            const statuses = statusResponse.data.data.leadTypes || [];
            if (statuses.length > 0) {
              setLeadStatus(statuses[0].id);
            }
          }
        } catch (err) {
          console.error('Error fetching lead types:', err);
        }
      }

      // Create lead data matching the working AddLeadModal format exactly
      const leadData = {
        first_name: newClientForm.name.split(' ')[0] || '',
        last_name: newClientForm.name.split(' ').slice(1).join(' ') || '',
        email: newClientForm.email,
        phone: newClientForm.phone,
        lead_source: 'Manual Entry',
        lead_types_id: leadType || 1, // Use fetched lead type or default to 1
        lead_type_status_id: leadStatus || 1 // Use fetched status or default to 1
      };

      console.log('Creating lead with data:', leadData);
      console.log('axiosInstance type:', typeof axiosInstance);
      console.log('axiosInstance.post type:', typeof axiosInstance.post);
      
      // Log what we're actually sending
      const debugData = { ...leadData };
      console.log('Debug - data being sent:', debugData);

      const response = await axiosInstance.post('/leads', leadData);
      
      console.log('Lead creation response:', response.data);

      if (response.data.success && response.data.data?.leads?.length > 0) {
        const newLead = response.data.data.leads[0];
        // Transform lead to client format
        const newClient = {
          id: newLead.id,
          name: `${newLead.lead_first_name || ''} ${newLead.lead_last_name || ''}`.trim() || newLead.lead_email || 'Unknown',
          email: newLead.lead_email || '',
          phone: newLead.lead_phone || '',
          lead_id: newLead.id,
          leadId: newLead.id
        };
        
        setClients([...clients, newClient]);
        setSelectedClient(newClient);
        setFormData({ ...formData, client_id: newLead.id });
        setClientSearchTerm(newClient.name || '');
        setShowAddClientModal(false);
        resetAddClientForm();
        
        // Success feedback
        const toast = document.createElement('div');
        toast.className = 'toast-notification';
        toast.textContent = 'Lead added successfully!';
        document.body.appendChild(toast);
        setTimeout(() => document.body.removeChild(toast), 3000);
      } else {
        console.error('Unexpected response format:', response.data);
        alert('Failed to add lead - unexpected response format');
      }
    } catch (error: any) {
      console.error('Error adding lead:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to add lead';
      alert(errorMessage);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.client_id || !selectedClient) {
      setError('Please select a lead');
      return;
    }

    if (!formData.client_type) {
      setError('Please select a client type');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      
      // When creating a transaction, pass all the lead data
      // The backend will create a new client record from this lead
      const transactionData: any = {
        type: formData.type,
        lead_id: selectedClient?.lead_id || selectedClient?.id,  // Lead ID from leads table
        client_type: formData.client_type,
        client_name: selectedClient?.name || '',                  // Full name from lead
        client_email: selectedClient?.email || '',                // Email from lead
        client_phone: selectedClient?.phone || '',                // Phone from lead
        notes: formData.notes
      };
      
      console.log('📤 Submitting transaction with lead data:', {
        lead_id: transactionData.lead_id,
        name: transactionData.client_name,
        email: transactionData.client_email,
        phone: transactionData.client_phone
      });

      // Only add property_data if there's property information
      if (formData.property_address) {
        transactionData.property_data = {
          address: formData.property_address,
          city: formData.property_city,
          state: formData.property_state,
          zip: formData.property_zip,
          price: formData.price
        };
      }

      const response = await axiosInstance.post('/transactions', transactionData);

      if (response.data.success) {
        setSuccess(true);
        const transactionId = response.data.transaction.id;
        
        setTimeout(() => {
          if (onTransactionCreated) {
            onTransactionCreated(transactionId);
          } else {
            navigate(`/agent-portal/transactions/${transactionId}`);
          }
        }, 1500);
      }
    } catch (err: any) {
      console.error('❌ Transaction creation error:', err);
      console.error('❌ Error response:', err.response?.data);
      
      // Handle specific error cases
      if (err.response?.data?.code === 'BROKERAGE_REQUIRED') {
        setError('You need to be associated with a brokerage to create transactions. Please contact your administrator.');
      } else {
        setError(err.response?.data?.error || 'Failed to create transaction');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (success) {
    return (
      <Container>
        <Row className="justify-content-center">
          <Col md={6}>
            <Card className="text-center border-0 shadow-sm">
              <Card.Body className="py-5">
                <FaCheckCircle size={60} className="text-success mb-3" />
                <h3 className="mb-3">Transaction Created!</h3>
                <p className="text-muted">Your new transaction has been created successfully.</p>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <div className="d-flex align-items-center mb-4">
          <Button 
            variant="link" 
            onClick={onBack || (() => navigate('/agent-portal'))}
            className="p-0 me-3 text-decoration-none"
          >
            <FaArrowLeft className="me-2" />
            Back to Transactions
          </Button>
        </div>

        <Row>
          <Col lg={8}>
            <Card className="shadow-sm">
              <Card.Header className="bg-white py-3">
                <h4 className="mb-0">
                  <FaFileContract className="me-2 text-primary" />
                  Create New Transaction
                </h4>
              </Card.Header>
              <Card.Body>
                {error && (
                  <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  {/* Transaction Type */}
                  <Form.Group className="mb-4">
                    <Form.Label>Transaction Type</Form.Label>
                    <Form.Select 
                      name="type"
                      value={formData.type}
                      onChange={(e) => {
                        handleChange(e);
                        // Reset client type when transaction type changes
                        setFormData(prev => ({ ...prev, client_type: '' }));
                      }}
                      size="lg"
                    >
                      <option value="residential_sale">Residential Sale</option>
                      <option value="residential_rental">Residential Rental</option>
                      <option value="commercial_sale">Commercial Sale</option>
                      <option value="commercial_rental">Commercial Rental</option>
                      <option value="referral">Referral</option>
                    </Form.Select>
                  </Form.Group>

                  {/* Client Type - Dynamic based on Transaction Type */}
                  <Form.Group className="mb-4">
                    <Form.Label>Client Type *</Form.Label>
                    <Form.Select 
                      name="client_type"
                      value={formData.client_type}
                      onChange={handleChange}
                      size="lg"
                      required
                    >
                      <option value="">Select client type...</option>
                      {formData.type === 'residential_sale' || formData.type === 'commercial_sale' ? (
                        <>
                          <option value="buyer">Buyer</option>
                          <option value="seller">Seller</option>
                          <option value="both">Both</option>
                        </>
                      ) : formData.type === 'residential_rental' || formData.type === 'commercial_rental' ? (
                        <>
                          <option value="tenant">Tenant</option>
                          <option value="landlord">Landlord</option>
                        </>
                      ) : formData.type === 'referral' ? (
                        <option value="other">Other</option>
                      ) : null}
                    </Form.Select>
                  </Form.Group>

                  {/* Client Selection with Dynamic Search */}
                  <Form.Group className="mb-4">
                    <Form.Label className="d-flex justify-content-between align-items-center">
                      <span>Select Lead *</span>
                      <Button 
                        variant="link" 
                        size="sm"
                        onClick={() => setShowAddClientModal(true)}
                        className="p-0"
                      >
                        <FaUserPlus className="me-1" />
                        Add New Client
                      </Button>
                    </Form.Label>
                    
                    <div className="position-relative" ref={clientSearchRef}>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          placeholder="Type to search leads by name, email, or phone..."
                          value={clientSearchTerm}
                          onChange={(e) => {
                            console.log('🔤 User typed:', e.target.value);
                            console.log('🔤 Total leads available:', clients.length);
                            setClientSearchTerm(e.target.value);
                          }}
                          size="lg"
                          required
                          className={selectedClient ? 'border-success' : ''}
                        />
                        <InputGroup.Text>
                          {loadingClients ? (
                            <Spinner animation="border" size="sm" />
                          ) : selectedClient ? (
                            <Button 
                              variant="link" 
                              className="p-0 text-danger"
                              onClick={clearClientSelection}
                            >
                              <FaTimes />
                            </Button>
                          ) : (
                            <FaSearch />
                          )}
                        </InputGroup.Text>
                      </InputGroup>
                      
                      {selectedClient && (
                        <div className="mt-2 p-2 bg-light rounded">
                          <small className="text-success">
                            <FaCheckCircle className="me-1" />
                            Selected: {selectedClient.name || 'Unknown'} • {selectedClient.email || 'No email'} • {selectedClient.phone || 'No phone'}
                          </small>
                        </div>
                      )}
                      
                      {/* Search Results Dropdown */}
                      {showClientDropdown && clientSearchResults.length > 0 && (
                        <div 
                          className="position-absolute w-100 mt-1 shadow-sm border rounded bg-white"
                          style={{ maxHeight: '300px', overflowY: 'auto', zIndex: 1000 }}
                        >
                          <ListGroup variant="flush">
                            {clientSearchResults.map(client => (
                              <ListGroup.Item
                                key={client.id}
                                action
                                onClick={() => selectClient(client)}
                                className="py-3"
                              >
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <strong>{client.name || 'Unknown Client'}</strong>
                                    <div className="text-muted small">
                                      {client.email || 'No email'} • {client.phone || 'No phone'}
                                    </div>
                                  </div>
                                  {client.lead_id && (
                                    <Badge bg="info" className="ms-2">Lead</Badge>
                                  )}
                                </div>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        </div>
                      )}
                      
                      {showClientDropdown && clientSearchTerm && clientSearchResults.length === 0 && !selectedClient && (
                        <div className="position-absolute w-100 mt-1 shadow-sm border rounded bg-white p-3">
                          <p className="text-muted mb-2">
                            {loadingClients ? (
                              <>Loading leads...</>
                            ) : (
                              <>No leads found matching "{clientSearchTerm}"</>
                            )}
                          </p>
                          <Button 
                            variant="primary" 
                            size="sm"
                            onClick={() => setShowAddClientModal(true)}
                          >
                            <FaUserPlus className="me-1" />
                            Add as New Lead
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    <Form.Text className="text-muted">
                      Start typing to search your leads by name, email, or phone
                    </Form.Text>
                  </Form.Group>

                  {/* Property Information (Optional) */}
                  <div className="border rounded p-3 mb-4 bg-light">
                    <h6 className="mb-3">
                      <FaHome className="me-2" />
                      Property Information (Optional)
                    </h6>
                    
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Property Address</Form.Label>
                          <Form.Control
                            type="text"
                            name="property_address"
                            value={formData.property_address}
                            onChange={handleChange}
                            placeholder="123 Main St"
                          />
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>City</Form.Label>
                          <Form.Control
                            type="text"
                            name="property_city"
                            value={formData.property_city}
                            onChange={handleChange}
                            placeholder="Los Angeles"
                          />
                        </Form.Group>
                      </Col>
                      
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>State</Form.Label>
                          <Form.Control
                            type="text"
                            name="property_state"
                            value={formData.property_state}
                            onChange={handleChange}
                            placeholder="CA"
                            maxLength={2}
                          />
                        </Form.Group>
                      </Col>
                      
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>ZIP Code</Form.Label>
                          <Form.Control
                            type="text"
                            name="property_zip"
                            value={formData.property_zip}
                            onChange={handleChange}
                            placeholder="90001"
                            maxLength={5}
                          />
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Price</Form.Label>
                          <Form.Control
                            type="text"
                            name="price"
                            value={formData.price}
                            onChange={handleChange}
                            placeholder="$500,000"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {/* Notes */}
                  <Form.Group className="mb-4">
                    <Form.Label>Notes (Optional)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Any additional notes about this transaction..."
                    />
                  </Form.Group>

                  {/* Submit Buttons */}
                  <div className="d-flex justify-content-end gap-2">
                    <Button 
                      variant="outline-secondary" 
                      onClick={onBack || (() => navigate('/agent-portal'))}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="primary" 
                      type="submit"
                      disabled={loading || !formData.client_id}
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <FaCheckCircle className="me-2" />
                          Create Transaction
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* Quick Tips */}
          <Col lg={4}>
            <Card className="shadow-sm">
              <Card.Header className="bg-white">
                <h5 className="mb-0">Quick Tips</h5>
              </Card.Header>
              <Card.Body>
                <ul className="list-unstyled">
                  <li className="mb-3">
                    <FaUser className="text-primary me-2" />
                    <strong>Client Required:</strong> Every transaction must be associated with a client.
                  </li>
                  <li className="mb-3">
                    <FaHome className="text-primary me-2" />
                    <strong>Property Optional:</strong> You can add property details now or update them later.
                  </li>
                  <li className="mb-3">
                    <FaFileContract className="text-primary me-2" />
                    <strong>Documents Next:</strong> After creating the transaction, you'll be able to add documents.
                  </li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Client Modal */}
      <Modal 
        show={showAddClientModal} 
        onHide={() => {
          setShowAddClientModal(false);
          resetAddClientForm();
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Lead</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4">
            <h6 className="mb-3">Search Existing Leads or Create New</h6>
            <Form.Group>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  value={leadSearchTerm}
                  onChange={(e) => setLeadSearchTerm(e.target.value)}
                />
                <InputGroup.Text>
                  {searchingLeads ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FaSearch />
                  )}
                </InputGroup.Text>
              </InputGroup>
              <Form.Text className="text-muted">
                Start typing to search your leads database
              </Form.Text>
            </Form.Group>
            
            {leadSearchTerm && leads.length === 0 && !searchingLeads && (
              <Alert variant="info" className="mt-3">
                No leads found. Fill in the client details below.
              </Alert>
            )}
            
            {leads.length > 0 && (
              <ListGroup className="mt-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {leads.map(lead => (
                  <ListGroup.Item 
                    key={lead.id} 
                    action
                    onClick={() => selectLeadForClient(lead)}
                    active={selectedLead?.id === lead.id}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{lead.lead_first_name} {lead.lead_last_name || ''}</strong>
                        <div className="small text-muted">
                          {lead.lead_email || 'No email'} • {lead.lead_phone || 'No phone'}
                        </div>
                      </div>
                      {lead.lead_source && (
                        <Badge bg="secondary" className="ms-2">{lead.lead_source}</Badge>
                      )}
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>

          <hr />

          <h6 className="mb-3">Client Details</h6>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Full Name *</Form.Label>
              <Form.Control
                type="text"
                value={newClientForm.name}
                onChange={(e) => setNewClientForm({...newClientForm, name: e.target.value})}
                required
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={newClientForm.email}
                    onChange={(e) => setNewClientForm({...newClientForm, email: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    value={newClientForm.phone}
                    onChange={(e) => setNewClientForm({...newClientForm, phone: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Transaction Type</Form.Label>
                  <Form.Select
                    value={newClientForm.transactionType}
                    onChange={(e) => {
                      setNewClientForm({...newClientForm, transactionType: e.target.value as any});
                      // Reset client type when transaction type changes
                      if (e.target.value === 'referral') {
                        setNewClientForm({...newClientForm, transactionType: e.target.value as any, relationshipType: 'other'});
                      } else if (e.target.value.includes('rental')) {
                        setNewClientForm({...newClientForm, transactionType: e.target.value as any, relationshipType: 'tenant'});
                      } else {
                        setNewClientForm({...newClientForm, transactionType: e.target.value as any, relationshipType: 'buyer'});
                      }
                    }}
                  >
                    <option value="residential_sale">Residential Sale</option>
                    <option value="residential_rental">Residential Rental</option>
                    <option value="commercial_sale">Commercial Sale</option>
                    <option value="commercial_rental">Commercial Rental</option>
                    <option value="referral">Referral</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Client Type</Form.Label>
                  <Form.Select
                    value={newClientForm.relationshipType}
                    onChange={(e) => setNewClientForm({...newClientForm, relationshipType: e.target.value as any})}
                  >
                    {newClientForm.transactionType === 'residential_sale' || newClientForm.transactionType === 'commercial_sale' ? (
                      <>
                        <option value="buyer">Buyer</option>
                        <option value="seller">Seller</option>
                        <option value="both">Both</option>
                      </>
                    ) : newClientForm.transactionType === 'residential_rental' || newClientForm.transactionType === 'commercial_rental' ? (
                      <>
                        <option value="tenant">Tenant</option>
                        <option value="landlord">Landlord</option>
                      </>
                    ) : newClientForm.transactionType === 'referral' ? (
                      <option value="other">Other</option>
                    ) : null}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowAddClientModal(false);
            resetAddClientForm();
          }}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={addNewClient}
            disabled={!newClientForm.name}
          >
            Add Lead
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .toast-notification {
          position: fixed;
          top: 20px;
          right: 20px;
          background: #28a745;
          color: white;
          padding: 12px 20px;
          border-radius: 4px;
          box-shadow: 0 2px 5px rgba(0,0,0,0.2);
          z-index: 9999;
        }
      `}</style>
    </>
  );
};

export default NewTransactionEnhanced;